import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {Button, Spinner} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useAnalytics} from '@github-ui/use-analytics'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import styles from './PullRequestsProcessingIndicator.module.css'
import {useQuery} from '@github-ui/react-query'

export interface PullRequestsProcessingIndicatorProps {
  processingIndicatorUrl: string
  repositoryId: number
  pullRequestId: number
}

function differenceInSeconds(laterDate: Date, earlierDate: Date) {
  return Math.round(laterDate.getTime() - earlierDate.getTime()) / 1000
}

export interface ProcessingState {
  stale: boolean
  latest_unsynced_push_to_head_ref_at: string | null // date
}

const initialProcessingState: ProcessingState = {
  stale: false,
  latest_unsynced_push_to_head_ref_at: null,
}

/**
 * Renders a spinner with information if the pull request is stale and the last
 * push was more than 20 seconds ago. Handles its own data fetching from an
 * internal JSON endpoint.
 *
 * This is a React partial rendered by the Rails description partial
 * (app/views/pull_requests/_description.html.erb) and the React PullRequestHeaderSummary component.
 */
export function PullRequestsProcessingIndicatorWithDataFetching({
  processingIndicatorUrl,
  repositoryId,
  pullRequestId,
}: PullRequestsProcessingIndicatorProps) {
  // This query is not a good candidate for persistence since showing old data would not be useful here
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister
  const {data: processingState, isError} = useQuery({
    queryKey: ['pullRequestProcessingInfo', processingIndicatorUrl],
    queryFn: async () => {
      const response = await verifiedFetchJSON(processingIndicatorUrl)
      if (!response.ok) throw new Error('Failed to fetch processing info')
      const result: ProcessingState = await response.json()
      return result
    },
    placeholderData: initialProcessingState,
  })

  if (isError) return null

  const {stale, latest_unsynced_push_to_head_ref_at} = processingState ?? initialProcessingState

  if (!stale || !latest_unsynced_push_to_head_ref_at) return null

  const now = new Date()
  const lastPushDate = new Date(latest_unsynced_push_to_head_ref_at)
  const secondsSinceLastPush = differenceInSeconds(now, lastPushDate)
  const oneDayinSeconds = 24 * 60 * 60

  if (secondsSinceLastPush < 20 || secondsSinceLastPush >= oneDayinSeconds) return null

  return (
    <div className="border-left pl-1">
      <AnalyticsProvider appName="pull-requests-processing-indicator" category="render" metadata={{}}>
        <PullRequestsProcessingIndicator
          pullRequestId={pullRequestId}
          repositoryId={repositoryId}
          secondsSinceLastPush={secondsSinceLastPush}
        />
      </AnalyticsProvider>
    </div>
  )
}

/**
 * Renders a spinner with information if the pull request is stale and the last push was more than 20 seconds ago
 * Presentational component that can be wrapped inside of a Relay component
 */
function PullRequestsProcessingIndicator({
  pullRequestId,
  repositoryId,
  secondsSinceLastPush,
}: {secondsSinceLastPush: number} & Pick<PullRequestsProcessingIndicatorProps, 'repositoryId' | 'pullRequestId'>) {
  const {sendAnalyticsEvent} = useAnalytics()

  sendAnalyticsEvent('pull_requests.processing_indicator', '', {
    repositoryId,
    pullRequestId,
    secondsSinceLastPush,
  })

  return (
    <Tooltip text="Recent push is being processed and will appear soon" direction="s">
      <Button as="a" href="https://gh.io/pr-sync-in-progress" variant="invisible" className={styles.button}>
        <div className={styles.indicator}>
          <Spinner size="small" />
          <span className={styles.text}>Processing updates</span>
        </div>
      </Button>
    </Tooltip>
  )
}
